import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Spin, message, Empty, Modal, Input, Descriptions, Table, Typography } from "antd";
import { 
  ArrowLeftOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  LinkOutlined,
  FolderOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  EyeOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import ProjectRecommendations from "../../Customer/Projects/ProjectRecommendations";

const { TextArea } = Input;

const ProjectDetailsWrapper = styled.div`
  padding: 24px;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;

      h2 {
        margin: 0;
        font-size: 24px;
        color: #014c75;
      }
    }

    .header-actions {
      display: flex;
      gap: 12px;
    }
  }

  .project-id-badge {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 8px;
  }

  .engagement-link-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f0f7ff;
    border: 1px solid #d6e4ff;
    border-radius: 8px;
    margin-bottom: 24px;
    cursor: pointer;

    &:hover {
      background: #e6f0ff;
    }

    .engagement-info {
      flex: 1;

      .label {
        font-size: 12px;
        color: #8c8c8c;
      }

      .title {
        font-size: 14px;
        font-weight: 500;
        color: #014c75;
      }
    }
  }

  .status-cards {
    margin-bottom: 24px;

    .status-card {
      border-radius: 12px;
      text-align: center;
      padding: 16px;

      .status-label {
        font-size: 12px;
        color: #8c8c8c;
        margin-bottom: 4px;
      }

      .status-value {
        font-size: 20px;
        font-weight: 600;

        &.good {
          color: #52c41a;
        }

        &.warning {
          color: #faad14;
        }

        &.critical {
          color: #ff4d4f;
        }
      }
    }
  }

  .details-section {
    margin-bottom: 24px;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #014c75;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #f0f0f0;
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;

      .info-item {
        .label {
          font-size: 12px;
          color: #8c8c8c;
          margin-bottom: 4px;
        }

        .value {
          font-size: 14px;
          color: #262626;
          font-weight: 500;
        }
      }
    }
  }

  .org-link {
    color: #1890ff;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .jobs-section {
    border-radius: 12px;
    margin-bottom: 24px;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .section-title {
        font-size: 16px;
        font-weight: 600;
        color: #014c75;
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #8c8c8c;
    }
  }
`;

const statusColors = {
  pending_approval: "orange",
  active: "green",
  rejected: "red",
  completed: "default",
};

const jobStatusColors = {
  draft: "default",
  pending_approval: "orange",
  open: "blue",
  in_progress: "processing",
  filled: "green",
  closed: "default",
  on_hold: "gold",
};

const statusLabels = {
  pending_approval: "Pending Approval",
  active: "Active",
  rejected: "Rejected",
  completed: "Completed",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "₹0";
  return `₹ ${parseFloat(value).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AdminProjectDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_PROJECT}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(response.data.project);
    } catch (error) {
      console.error("Error fetching project:", error);
      message.error("Failed to load project details");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = () => {
    Modal.confirm({
      title: "Approve Project",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: `Are you sure you want to approve "${project.title}"? This will activate the project.`,
      okText: "Approve",
      okButtonProps: { style: { background: "#52c41a", borderColor: "#52c41a" } },
      onOk: async () => {
        setActionLoading(true);
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.APPROVE_PROJECT}/${id}/approve`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Project approved successfully");
          fetchProject();
        } catch (error) {
          message.error(error.response?.data?.error || "Failed to approve project");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      message.error("Please provide a rejection reason");
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.REJECT_PROJECT}/${id}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Project rejected");
      setRejectModalVisible(false);
      setRejectionReason("");
      fetchProject();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to reject project");
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = () => {
    Modal.confirm({
      title: "Complete Project",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: `Are you sure you want to mark "${project.title}" as completed?`,
      okText: "Complete",
      onOk: async () => {
        setActionLoading(true);
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.COMPLETE_PROJECT}/${id}/complete`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Project marked as completed");
          fetchProject();
        } catch (error) {
          message.error(error.response?.data?.error || "Failed to complete project");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <ProjectDetailsWrapper>
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      </ProjectDetailsWrapper>
    );
  }

  if (!project) {
    return (
      <ProjectDetailsWrapper>
        <Empty description="Project not found" />
      </ProjectDetailsWrapper>
    );
  }

  return (
    <ProjectDetailsWrapper>
      <div className="header">
        <div className="header-left">
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          />
          <div>
            <p className="project-id-badge">{project.id}</p>
            <h2>{project.title}</h2>
          </div>
        </div>
        <div className="header-actions">
          <Tag color={statusColors[project.status]} style={{ fontSize: 14, padding: "4px 12px" }}>
            {statusLabels[project.status]}
          </Tag>
          {project.status === "pending_approval" && (
            <>
              <Button 
                type="primary"
                icon={<CheckCircleOutlined />}
                style={{ background: "#52c41a", borderColor: "#52c41a" }}
                onClick={handleApprove}
                loading={actionLoading}
              >
                Approve
              </Button>
              <Button 
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => setRejectModalVisible(true)}
                loading={actionLoading}
              >
                Reject
              </Button>
            </>
          )}
          {project.status === "active" && (
            <Button 
              icon={<CheckCircleOutlined />}
              onClick={handleComplete}
              loading={actionLoading}
            >
              Mark Complete
            </Button>
          )}
        </div>
      </div>

      {project.engagement && (
        <div 
          className="engagement-link-banner"
          onClick={() => navigate(`/admin/engagement/${project.engagementId}`)}
        >
          <LinkOutlined style={{ fontSize: 20, color: "#1890ff" }} />
          <div className="engagement-info">
            <div className="label">Parent Engagement</div>
            <div className="title">{project.engagement.title}</div>
          </div>
          <Tag color={project.engagement.status === "active" ? "green" : "orange"}>
            {project.engagement.status}
          </Tag>
        </div>
      )}

      {project.engagement?.organization && (
        <Card style={{ marginBottom: 16 }}>
          <Descriptions title="Organization" column={3}>
            <Descriptions.Item label="Name">
              <span 
                className="org-link"
                onClick={() => navigate(`/admin/organization/${encodeURIComponent(project.engagement.organization.organizationName)}`)}
              >
                {project.engagement.organization.organizationName}
              </span>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {project.status === "active" && project.health && (
        <Row gutter={16} className="status-cards">
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <div className="status-label">Overall Health</div>
              <div className={`status-value ${project.health.overall}`}>
                {project.health.overall === "good" && "Good"}
                {project.health.overall === "warning" && "Warning"}
                {project.health.overall === "critical" && "Critical"}
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <div className="status-label">Timeline</div>
              <div className={`status-value ${project.health.timeline}`}>
                {project.health.timeline === "good" && "On Track"}
                {project.health.timeline === "warning" && "Ending Soon"}
                {project.health.timeline === "critical" && "Overdue"}
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <div className="status-label">Budget</div>
              <div className={`status-value ${project.health.budget}`}>
                {project.health.budget === "good" && "Within Budget"}
                {project.health.budget === "warning" && "80%+ Used"}
                {project.health.budget === "critical" && "Exceeded"}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {project.status === "rejected" && project.rejectionReason && (
        <Card style={{ marginBottom: 24, background: "#fff2f0", borderColor: "#ffccc7" }}>
          <div style={{ color: "#cf1322", fontWeight: 500, marginBottom: 8 }}>Rejection Reason</div>
          <p style={{ margin: 0 }}>{project.rejectionReason}</p>
        </Card>
      )}

      <Card style={{ marginBottom: 24 }}>
        <div className="details-section">
          <div className="section-title">Project Information</div>
          <div className="info-grid">
            <div className="info-item">
              <div className="label">Budget Allocated</div>
              <div className="value">{formatCurrency(project.budgetAllocated)}</div>
            </div>
            <div className="info-item">
              <div className="label">Budget Spent</div>
              <div className="value">{formatCurrency(project.budgetSpent)}</div>
            </div>
            <div className="info-item">
              <div className="label">Expected Start</div>
              <div className="value">{formatDate(project.expectedStartDate)}</div>
            </div>
            <div className="info-item">
              <div className="label">Expected End</div>
              <div className="value">{formatDate(project.expectedEndDate)}</div>
            </div>
            <div className="info-item">
              <div className="label">Actual Start</div>
              <div className="value">{formatDate(project.actualStartDate)}</div>
            </div>
            <div className="info-item">
              <div className="label">Actual End</div>
              <div className="value">{formatDate(project.actualEndDate)}</div>
            </div>
            <div className="info-item">
              <div className="label">Total Jobs</div>
              <div className="value">{project.jobCount || 0}</div>
            </div>
            <div className="info-item">
              <div className="label">Total Hired</div>
              <div className="value">{project.totalHired || 0}</div>
            </div>
            {project.country && (
              <div className="info-item">
                <div className="label">Country</div>
                <div className="value">{project.country}</div>
              </div>
            )}
          </div>
        </div>

        {project.description && (
          <div className="details-section">
            <div className="section-title">Description</div>
            <p style={{ margin: 0, color: "#595959" }}>{project.description}</p>
          </div>
        )}
      </Card>

      {(project.status === "active" || project.status === "pending_approval") && 
        (project.creationMode === "STAFF_AUGMENTATION"
          ? true
          : (project.primaryTechnologies && project.primaryTechnologies.length > 0)) && (
        <ProjectRecommendations 
          projectId={project.id} 
          projectStatus={project.status}
          project={project}
          isAdmin={true}
        />
      )}

      <Card className="jobs-section">
        <div className="section-header">
          <div className="section-title">
            <TeamOutlined /> Jobs ({project.jobs?.length || 0})
          </div>
        </div>

        {project.jobs && project.jobs.length > 0 ? (
          <Table
            columns={[
              {
                title: "Job ID",
                dataIndex: "id",
                key: "id",
                width: 120,
                render: (id) => <Text code style={{ fontSize: 12 }}>{id}</Text>,
              },
              {
                title: "Role",
                dataIndex: "role",
                key: "role",
                render: (role) => <Text strong>{role}</Text>,
              },
              {
                title: "Experience",
                dataIndex: "experienceRange",
                key: "experienceRange",
                width: 120,
              },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                width: 120,
                render: (status) => (
                  <Tag color={jobStatusColors[status] || "default"}>
                    {status?.replace("_", " ").toUpperCase()}
                  </Tag>
                ),
              },
              {
                title: "Positions",
                dataIndex: "noOfOpenings",
                key: "noOfOpenings",
                width: 90,
                align: "center",
              },
              {
                title: "Action",
                key: "action",
                width: 80,
                render: (_, record) => (
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/admin/job-requirments/job-details/${record.id}`)}
                  >
                    View
                  </Button>
                ),
              },
            ]}
            dataSource={project.jobs}
            rowKey="id"
            pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            size="small"
          />
        ) : (
          <div className="empty-state">
            <TeamOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <div>No jobs created under this project</div>
          </div>
        )}
      </Card>

      <Modal
        title="Reject Project"
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        onOk={handleRejectSubmit}
        okText="Reject"
        okButtonProps={{ danger: true, loading: actionLoading }}
      >
        <p>Please provide a reason for rejecting "{project?.title}":</p>
        <TextArea
          rows={4}
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="Enter rejection reason..."
        />
      </Modal>
    </ProjectDetailsWrapper>
  );
};

export default AdminProjectDetails;
